import { verticalPanelPlacement } from '../../hooks/useResponsivePanelPlacement';
import {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect,
  useId,
} from 'react';
import { createPortal } from 'react-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { ClientProject } from '@packages/types';
import { queryKeys, getNextPageParam, flattenPages, DEFAULT_LIST_PAGE_SIZE } from '@packages/api';
import { listClientProjects } from '@packages/api/endpoints/client-project.js';
import { useKeyboardNavHighlight } from '../../hooks/useKeyboardNavHighlight';
import { useCopy } from '../../hooks/useCopy';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Reveal } from '../ui/Reveal';
import { useRevealGroup } from '../../hooks/useRevealGroup';
import { MatterPickerSkeleton } from './MatterPickerSkeleton';
import { SearchIcon } from '../ui/SearchIcon';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const GAP = 4;
const MARGIN = 8;
const MIN_PANEL_HEIGHT = 140;

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * 'inline' (default): the full picker panel (search input + grouped list) is
 * always-open and rendered in-flow. Used by the timer widget, which opens the
 * picker via a popover it controls.
 *
 * 'dropdown': a single persistent search input is shown (it IS both the
 * trigger and the live search field). Focusing or typing opens the grouped
 * results panel in a portaled dropdown directly below the input. Selecting
 * commits + closes; Escape or outside-click closes without selecting.
 * Used by the expense form so the Attribution section stays compact until the
 * user actively picks a matter.
 */
export type MatterSelectorVariant = 'inline' | 'dropdown';

/** The data shape returned on selection – both callers consume this object. */
export interface SelectedMatter {
  id: string;
  name: string;
  clientId: string;
  clientName: string;
}

export interface MatterSelectorProps {
  accountId: string;
  /**
   * Called when the user picks a matter from the grouped list.
   * Receives the full identity so both callers – timer (needs clientId for
   * TimerContext) and expense form (needs id for clientProjectId FK) – get
   * exactly what they need without a second lookup.
   */
  onSelect: (matter: SelectedMatter) => void;
  /**
   * When provided, a "Cancel" button appears at the bottom of the picker
   * panel and calls this handler. Used by the timer widget to dismiss the
   * picker without selecting; the expense form omits it.
   */
  onCancel?: () => void;
  /**
   * When truthy, the committed-selection display is rendered instead of the
   * picker panel. Clearing calls onClear. Pass null (no user action yet) or
   * undefined to keep the picker visible.
   */
  selectedMatter?: SelectedMatter | null;
  /**
   * Called when the user clears the committed selection via the X button.
   * Required when selectedMatter may be truthy; no-op if omitted.
   */
  onClear?: () => void;
  /**
   * When true, the search input receives focus on mount. Set by the timer
   * widget (opened via explicit user click); leave false in embedded form
   * contexts where stealing focus would be jarring.
   */
  autoFocus?: boolean;
  /** Disables interaction (e.g. a read-only expense form). */
  disabled?: boolean;
  /** See MatterSelectorVariant. Defaults to 'inline'. */
  variant?: MatterSelectorVariant;
  /**
   * A pinned first option for "no matter" (dropdown variant only), shown while
   * the search box is empty. When `selected`, the committed display reads
   * `label` and clearing calls `onClear`.
   */
  noneOption?: { label: string; selected: boolean; onSelect: () => void };
}

// ---------------------------------------------------------------------------
// useMatterGroups – shared infinite query + client-grouping logic.
// Used by both PickerPanel (inline mode) and DropdownInput (dropdown mode).
// ---------------------------------------------------------------------------

function useMatterGroups(accountId: string, debouncedSearch: string) {
  const {
    data: matterPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery({
    queryKey: [...queryKeys.clientProjects(accountId), 'picker', debouncedSearch],
    queryFn: ({ pageParam }) =>
      listClientProjects(accountId, {
        cursor: pageParam,
        limit: DEFAULT_LIST_PAGE_SIZE,
        search: debouncedSearch || undefined,
        sortField: 'clientName',
        sortDirection: 'asc',
      }),
    getNextPageParam,
    initialPageParam: undefined as string | undefined,
    enabled: !!accountId,
  });

  const matters = useMemo(
    () => flattenPages(matterPages?.pages ?? []),
    [matterPages?.pages],
  );

  const groups = useMemo(() => {
    const grouped = new Map<
      string,
      { clientId: string; clientName: string; matters: ClientProject[] }
    >();

    for (const matter of matters) {
      if (matter.archivedAt) continue;
      const existing = grouped.get(matter.clientId);
      if (existing) {
        existing.matters.push(matter);
      } else {
        grouped.set(matter.clientId, {
          clientId: matter.clientId,
          clientName: matter.clientName,
          matters: [matter],
        });
      }
    }

    return [...grouped.values()].sort((a, b) =>
      a.clientName.localeCompare(b.clientName),
    );
  }, [matters]);

  return { groups, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError };
}

// ---------------------------------------------------------------------------
// Main component – switches between committed display, inline picker, and
// dropdown single-input picker.
// ---------------------------------------------------------------------------

export function MatterSelector({
  accountId,
  onSelect,
  onCancel,
  selectedMatter,
  onClear,
  autoFocus,
  disabled,
  variant = 'inline',
  noneOption,
}: MatterSelectorProps) {
  // Committed display takes precedence regardless of variant.
  if (selectedMatter) {
    return (
      <CommittedDisplay matter={selectedMatter} onClear={onClear} disabled={disabled} />
    );
  }

  if (noneOption?.selected) {
    return <CommittedDisplay matter={{ name: noneOption.label }} onClear={onClear} disabled={disabled} />;
  }

  if (variant === 'inline') {
    return (
      <PickerPanel
        accountId={accountId}
        onSelect={onSelect}
        onCancel={onCancel}
        autoFocus={autoFocus}
      />
    );
  }

  // Dropdown mode: single persistent search input + portaled grouped dropdown.
  return (
    <DropdownInput
      accountId={accountId}
      onSelect={onSelect}
      disabled={disabled}
      noneOption={noneOption}
    />
  );
}

// ---------------------------------------------------------------------------
// CommittedDisplay – shown when a matter has been selected.
// ---------------------------------------------------------------------------

function CommittedDisplay({
  matter,
  onClear,
  disabled,
}: {
  matter: Pick<SelectedMatter, 'name'> & { clientName?: string };
  onClear?: () => void;
  disabled?: boolean;
}) {
  const copy = useCopy();
  return (
    <div
      className="flex items-center gap-2 rounded-lg border border-border bg-bg-primary px-3 py-2"
      data-testid="matter-committed"
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-secondary">{matter.name}</div>
        {matter.clientName && <div className="truncate text-xs text-text-muted">{matter.clientName}</div>}
      </div>
      {onClear && !disabled && (
        <button
          type="button"
          onClick={onClear}
          className="shrink-0 text-text-muted transition-colors hover:text-error"
          aria-label={copy('project:selector.clearSelectionAriaLabel')}
        >
          <XIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PickerPanel – search input + client-grouped matter list.
// Always-open, rendered in-flow. Used by 'inline' mode (timer widget).
// ---------------------------------------------------------------------------

function PickerPanel({
  accountId,
  onSelect,
  onCancel,
  autoFocus,
}: {
  accountId: string;
  onSelect: (matter: SelectedMatter) => void;
  onCancel?: () => void;
  autoFocus?: boolean;
}) {
  const copy = useCopy();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  const { groups, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError } =
    useMatterGroups(accountId, debouncedSearch);
  const listRead = { isLoading };
  const reveal = useRevealGroup([listRead]);

  return (
    <div className="space-y-2">
      <Input
        id="matter-selector-search"
        type="text"
        placeholder={copy('project:selector.searchPlaceholder')}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        aria-label={copy('project:selector.searchAriaLabel')}
        autoFocus={autoFocus}
      />

      <div
        className="max-h-48 overflow-y-auto rounded-lg border border-border bg-bg-primary"
        role="listbox"
        aria-label={copy('project:selector.listboxAriaLabel')}
      >
        <Reveal group={reveal} read={listRead} placeholder={<MatterPickerSkeleton size="compact" />}>
          {groups.length === 0 || isError ? (
            <div className="px-3 py-4 text-center text-xs text-text-muted">
              {isError
                ? copy('project:selector.couldNotLoad')
                : searchQuery
                  ? 'No matches'
                  : copy('project:selector.noActiveProjects')}
            </div>
          ) : (
            <>
              {groups.map(({ clientId, clientName, matters: groupMatters }) => (
                <div key={clientId} role="group" aria-label={clientName}>
                  <div className="sticky top-0 bg-bg-muted px-3 py-1.5 text-xs font-medium text-text-muted">
                    {clientName}
                  </div>
                  {groupMatters.map((matter) => (
                    <button
                      key={matter.id}
                      type="button"
                      role="option"
                      aria-selected={false}
                      onClick={() =>
                        onSelect({
                          id: matter.id,
                          name: matter.name,
                          clientId,
                          clientName,
                        })
                      }
                      className="w-full px-3 py-1.5 text-left text-sm text-secondary transition-colors hover:bg-bg-muted"
                    >
                      {matter.name}
                    </button>
                  ))}
                </div>
              ))}
              {hasNextPage && (
                <button
                  type="button"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="w-full border-t border-border py-2 text-center text-xs text-text-muted transition-colors hover:bg-bg-subtle disabled:opacity-50"
                >
                  {isFetchingNextPage ? 'Loading...' : 'Load more'}
                </button>
              )}
            </>
          )}
        </Reveal>
      </div>

      {onCancel && (
        <Button variant="ghost" size="sm" onClick={onCancel} className="w-full">
          Cancel
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DropdownInput – single persistent search input + portaled grouped dropdown.
// Used in 'dropdown' mode. The input is always visible (no invisible trigger
// button, no second input). Focusing or typing opens the grouped results panel
// directly below; selecting or Escape/outside-click closes it.
// ---------------------------------------------------------------------------

interface DropdownPos {
  top: number | 'auto';
  bottom: number | 'auto';
  left: number;
  width: number;
  maxHeight: number;
}

function DropdownInput({
  accountId,
  onSelect,
  disabled,
  noneOption,
}: {
  accountId: string;
  onSelect: (matter: SelectedMatter) => void;
  disabled?: boolean;
  noneOption?: MatterSelectorProps['noneOption'];
}) {
  const copy = useCopy();
  // Stable per-instance id used for aria-controls / aria-activedescendant wiring.
  // Mirrors the Combobox.tsx + SearchPopover.tsx pattern.
  const listboxId = useId();

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPos, setDropdownPos] = useState<DropdownPos>({
    top: 0,
    bottom: 'auto',
    left: 0,
    width: 0,
    maxHeight: MIN_PANEL_HEIGHT,
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounce the search query to avoid hammering the API on every keystroke.
  // An empty query skips the delay so the next open shows the full list at once.
  const debouncedSearch = useDebouncedValue(searchQuery, 300, {
    flushWhen: (value) => value === '',
  });

  const { groups, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError } =
    useMatterGroups(accountId, debouncedSearch);
  const listRead = { isLoading };
  const reveal = useRevealGroup([listRead]);

  // Flattened matters list used for keyboard navigation indexing.
  const flatMatters = useMemo(
    () => groups.flatMap(({ matters }) => matters),
    [groups],
  );

  // The pinned "no matter" row takes index 0 when shown, shifting every matter down one.
  const showNone = !!noneOption && searchQuery === '';
  const noneOffset = showNone ? 1 : 0;

  // O(1) lookup: matter id → flat index for rendering data-flat-index attributes.
  const flatIndexByMatterId = useMemo(() => {
    const map = new Map<string, number>();
    flatMatters.forEach((m, i) => map.set(m.id, i + noneOffset));
    return map;
  }, [flatMatters, noneOffset]);

  // Keyboard nav hook: scrolls the highlighted row into view via the list's
  // own scrollTop (no window scroll), and gates hover so a stationary cursor
  // does not steal the keyboard highlight. The resolveItem callback locates
  // the highlighted row in the grouped list via data-flat-index attributes.
  const { markKeyboardNav, markPointerNav, shouldHoverHighlight } =
    useKeyboardNavHighlight({
      listRef,
      highlightedIndex,
      resolveItem: (list, index) =>
        list.querySelector<HTMLElement>(`[data-flat-index="${index}"]`),
    });

  // Position the portaled dropdown below (or above when space is tight).
  const updateDropdownPos = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const vertical = verticalPanelPlacement({
      viewportHeight: window.innerHeight,
      downwardTop: rect.bottom + GAP,
      upwardBottomEdge: rect.top - GAP,
      margin: MARGIN,
      flipThreshold: MIN_PANEL_HEIGHT,
      minHeight: MIN_PANEL_HEIGHT,
    });
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- layout measurement requires setState
    setDropdownPos({
      ...vertical,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updateDropdownPos();
    window.addEventListener('scroll', updateDropdownPos, { capture: true, passive: true });
    window.addEventListener('resize', updateDropdownPos);
    return () => {
      window.removeEventListener('scroll', updateDropdownPos, { capture: true });
      window.removeEventListener('resize', updateDropdownPos);
    };
  }, [isOpen, updateDropdownPos]);

  // Close on outside-click. Escape is handled inline in onKeyDown so the
  // document listener does not interfere with parent dialog Escape handlers.
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        listRef.current &&
        !listRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = useCallback(
    (matter: ClientProject) => {
      setIsOpen(false);
      setSearchQuery('');
      setHighlightedIndex(-1);
      inputRef.current?.blur();
      onSelect({
        id: matter.id,
        name: matter.name,
        clientId: matter.clientId,
        clientName: matter.clientName,
      });
    },
    [onSelect],
  );

  const handleSelectNone = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    setHighlightedIndex(-1);
    inputRef.current?.blur();
    noneOption?.onSelect();
  }, [noneOption]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        markKeyboardNav();
        setHighlightedIndex((prev) =>
          prev < flatMatters.length + noneOffset - 1 ? prev + 1 : prev,
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        markKeyboardNav();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (showNone && highlightedIndex === 0) {
          handleSelectNone();
        } else if (highlightedIndex >= 0 && flatMatters[highlightedIndex - noneOffset]) {
          handleSelect(flatMatters[highlightedIndex - noneOffset]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const panelStyle: React.CSSProperties = {
    position: 'fixed',
    top: dropdownPos.top,
    bottom: dropdownPos.bottom,
    left: dropdownPos.left,
    width: dropdownPos.width,
    maxHeight: dropdownPos.maxHeight,
  };

  return (
    <div ref={containerRef}>
      <div className="relative">
        <SearchIcon
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-light"
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-activedescendant={
            isOpen && highlightedIndex >= 0
              ? `${listboxId}-opt-${highlightedIndex}`
              : undefined
          }
          aria-label={copy('project:selector.searchAriaLabel')}
          aria-autocomplete="list"
          autoComplete="off"
          placeholder={copy('project:selector.searchPlaceholder')}
          value={searchQuery}
          disabled={disabled}
          className={[
            'w-full rounded-lg border border-border bg-bg-primary py-2 pl-9 pr-3 text-sm text-secondary',
            'placeholder:text-text-light',
            'focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20',
            'disabled:cursor-not-allowed disabled:opacity-50',
          ].join(' ')}
          onFocus={() => {
            if (!disabled) setIsOpen(true);
          }}
          onBlur={(e) => {
            // Close when focus leaves to an element outside the portaled list.
            // Option buttons carry tabIndex={-1} so they never steal focus on
            // click; this path is only reached on Tab key navigation away.
            const relatedTarget = e.relatedTarget as Node | null;
            if (!listRef.current?.contains(relatedTarget)) {
              setIsOpen(false);
              setSearchQuery('');
              setHighlightedIndex(-1);
            }
          }}
          onChange={(e) => {
            markPointerNav();
            setSearchQuery(e.target.value);
            setIsOpen(true);
            setHighlightedIndex(-1);
          }}
          onKeyDown={handleKeyDown}
        />
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={listRef}
            id={listboxId}
            role="listbox"
            aria-label={copy('project:selector.listboxAriaLabel')}
            style={panelStyle}
            className="z-[9999] overflow-y-auto rounded-lg border border-border bg-bg-primary shadow-lg"
            onMouseMove={markPointerNav}
          >
            {showNone && noneOption && (
              <button
                id={`${listboxId}-opt-0`}
                type="button"
                role="option"
                aria-selected={highlightedIndex === 0}
                tabIndex={-1}
                data-flat-index={0}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelectNone();
                }}
                onMouseEnter={() => {
                  if (shouldHoverHighlight()) setHighlightedIndex(0);
                }}
                className={[
                  'w-full border-b border-border px-3 py-2 text-left text-sm font-medium transition-colors',
                  highlightedIndex === 0 ? 'bg-primary/10 text-secondary' : 'text-secondary hover:bg-bg-muted',
                ].join(' ')}
              >
                {noneOption.label}
              </button>
            )}
            <Reveal group={reveal} read={listRead} placeholder={<MatterPickerSkeleton size="compact" />}>
              {groups.length === 0 || isError ? (
                <div className="px-3 py-4 text-center text-xs text-text-muted">
                  {isError
                    ? copy('project:selector.couldNotLoad')
                    : searchQuery
                      ? 'No matches'
                      : copy('project:selector.noActiveProjects')}
                </div>
              ) : (
                <>
                  {groups.map(({ clientId, clientName, matters: groupMatters }) => (
                    <div key={clientId} role="group" aria-label={clientName}>
                      <div className="sticky top-0 bg-bg-muted px-3 py-1.5 text-xs font-medium text-text-muted">
                        {clientName}
                      </div>
                      {groupMatters.map((matter) => {
                        const flatIndex = flatIndexByMatterId.get(matter.id) ?? -1;
                        return (
                          <button
                            key={matter.id}
                            id={`${listboxId}-opt-${flatIndex}`}
                            type="button"
                            role="option"
                            aria-selected={highlightedIndex === flatIndex}
                            // Not in the tab order: keyboard navigation is via
                            // arrow keys on the input (ARIA combobox pattern).
                            tabIndex={-1}
                            data-flat-index={flatIndex}
                            onMouseDown={(e) => {
                              // Prevent the input from blurring before handleSelect fires.
                              e.preventDefault();
                              handleSelect(matter);
                            }}
                            onMouseEnter={() => {
                              if (shouldHoverHighlight()) setHighlightedIndex(flatIndex);
                            }}
                            className={[
                              'w-full px-3 py-1.5 text-left text-sm transition-colors',
                              highlightedIndex === flatIndex
                                ? 'bg-primary/10 text-secondary'
                                : 'text-secondary hover:bg-bg-muted',
                            ].join(' ')}
                          >
                            {matter.name}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                  {hasNextPage && (
                    <button
                      type="button"
                      onClick={() => fetchNextPage()}
                      disabled={isFetchingNextPage}
                      className="w-full border-t border-border py-2 text-center text-xs text-text-muted transition-colors hover:bg-bg-subtle disabled:opacity-50"
                    >
                      {isFetchingNextPage ? 'Loading...' : 'Load more'}
                    </button>
                  )}
                </>
              )}
            </Reveal>
          </div>,
          document.body,
        )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
